import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../Modal';
import { Field, FormShell, type FieldErrors } from '../FormShell';
import { useToast } from '../Toast';
import type { StreamDto, WorkStreamUpsertDto } from '../../lib/types';
import { boardQueryKey, createStream, deleteStream, isApiStatus, problemDetail, updateStream } from '../../lib/workApi';
import { saveErrorState } from './formErrors';

interface StreamDialogProps {
  /** null = create a new stream. */
  streamId: number | null;
  /** The stream for `streamId`, looked up from the current board (null if it no longer exists). */
  stream: StreamDto | null;
  onClose: () => void;
}

const FORM_ID = 'stream-form';
const FIELDS = ['name', 'workerCount'] as const;

export function StreamDialog({ streamId, stream, onClose }: StreamDialogProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(stream?.name ?? '');
  const [workerCount, setWorkerCount] = useState(stream ? String(stream.workerCount) : '');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const onDone = () => {
    void qc.invalidateQueries({ queryKey: boardQueryKey });
    onClose();
  };

  const save = useMutation({
    mutationFn: (dto: WorkStreamUpsertDto) => (stream ? updateStream(stream.id, dto) : createStream(dto)),
    onSuccess: onDone,
    onError: (err) => {
      const next = saveErrorState(err, FIELDS);
      setFieldErrors(next.fieldErrors);
      setFormError(next.formError);
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) => deleteStream(id),
    onSuccess: onDone,
    onError: (err) => {
      setConfirmDelete(false);
      if (isApiStatus(err, 409)) {
        toast.error(problemDetail(err, 'This stream still has cards.'));
        void qc.invalidateQueries({ queryKey: boardQueryKey });
      } else {
        setFormError(problemDetail(err, 'Delete failed. Please try again.'));
      }
    },
  });

  // The stream was removed (e.g. by another user) after the dialog opened: never fall back to create.
  if (streamId !== null && !stream) {
    return (
      <Modal open onClose={onClose} title="Edit stream" size="sm"
        footer={
          <button type="button" autoFocus onClick={onClose}
            className="px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100">
            Close
          </button>
        }>
        <p className="text-slate-700">This stream no longer exists.</p>
      </Modal>
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    const toInt = (v: string) => (v.trim() === '' ? 0 : Number(v));
    save.mutate({ name: name.trim(), workerCount: toInt(workerCount) });
  }

  const cardCount = stream?.cards.length ?? 0;
  const busy = save.isPending || remove.isPending;

  const footer = (
    <>
      {stream && (
        <button
          type="button"
          disabled={busy || cardCount > 0}
          onClick={() => (confirmDelete ? remove.mutate(stream.id) : setConfirmDelete(true))}
          className="mr-auto px-3 py-1.5 rounded text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:hover:bg-transparent"
          aria-describedby={cardCount > 0 ? 'stream-delete-help' : undefined}
        >
          {confirmDelete ? 'Confirm delete' : 'Delete'}
        </button>
      )}
      <button type="button" onClick={onClose} className="px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100">
        Cancel
      </button>
      <button type="submit" form={FORM_ID} disabled={busy}
        className="px-3 py-1.5 rounded bg-atg-blue text-white hover:bg-atg-navy disabled:opacity-50">
        {save.isPending ? 'Saving...' : 'Save'}
      </button>
    </>
  );

  return (
    <Modal open onClose={onClose} title={stream ? 'Edit stream' : 'New stream'} size="sm" footer={footer}>
      <FormShell id={FORM_ID} onSubmit={onSubmit} formError={formError}>
        <Field label="Name" name="name" required maxLength={100} autoFocus value={name}
          onChange={(e) => setName(e.target.value)} errors={fieldErrors} />
        <Field label="Workers" name="workerCount" type="number" min={0} step={1} value={workerCount}
          onChange={(e) => setWorkerCount(e.target.value)} errors={fieldErrors} />
        {cardCount > 0 && (
          <p id="stream-delete-help" className="text-xs text-slate-500">
            Move or delete this stream's {cardCount} {cardCount === 1 ? 'card' : 'cards'} first.
          </p>
        )}
      </FormShell>
    </Modal>
  );
}
